/* Admin console · live bank balance of the shared vault pool vs. what the ledger says is locked. */
import { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Landmark, RefreshCw, AlertTriangle, Check } from "lucide-react";

export default function VaultPoolCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/vault-pool"); setData(data); setErr(""); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data && !err) return null;
  const short = data?.shortfall > 0;
  return (
    <section className="mt-10 bg-cream-warm border border-sand-warm rounded-2xl p-6" data-testid="admin-vault-pool">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-sage"><Landmark className="w-3.5 h-3.5" /> Vault pool · bank balance</div>
          <h2 className="font-serif text-2xl mt-1">Where locked money actually sits.</h2>
        </div>
        <button onClick={load} disabled={busy} className="inline-flex items-center gap-1 text-xs text-moss hover:text-ink" data-testid="admin-vault-pool-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {err && <p className="mt-3 text-sm text-clay" data-testid="admin-vault-pool-error">{err}</p>}
      {data && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-cream rounded-xl p-4 border border-sand">
            <div className="text-[11px] uppercase tracking-wider text-moss">Available at the bank</div>
            <div className="font-serif text-3xl mt-1" data-testid="admin-vault-pool-available">{data.available == null ? "—" : fmtMoney(data.available)}</div>
          </div>
          <div className="bg-cream rounded-xl p-4 border border-sand">
            <div className="text-[11px] uppercase tracking-wider text-moss">Ledger says locked (all bill locks)</div>
            <div className="font-serif text-3xl mt-1" data-testid="admin-vault-pool-ledger">{fmtMoney(data.ledger_bill_locks)}</div>
          </div>
          <div className={`rounded-xl p-4 border ${short ? "bg-clay-light border-clay/40" : "bg-forest/10 border-forest/30"}`}>
            <div className="text-[11px] uppercase tracking-wider text-moss">Shortfall</div>
            <div className="font-serif text-3xl mt-1 inline-flex items-center gap-2" data-testid="admin-vault-pool-shortfall">
              {short ? <AlertTriangle className="w-5 h-5 text-clay" /> : <Check className="w-5 h-5 text-forest" />}
              {data.shortfall == null ? "—" : fmtMoney(data.shortfall)}
            </div>
          </div>
        </div>
      )}
      <p className="mt-4 text-xs text-moss leading-relaxed">
        Releases can only move what's <strong className="text-ink">available at the bank</strong>. If the ledger is higher, unlocks release what's there and "Release &amp; delete" writes the rest off.
      </p>
    </section>
  );
}
